import { z } from 'zod';

import {
  ACTION_POLICY_RISK_LEVELS,
  ACTOR_MEMBERSHIP_TYPES,
  ACTOR_STATUS,
  ACTOR_TYPES,
  APPROVAL_MODES,
  AUTHORITY_STATUSES,
  CREDENTIAL_GRANT_STATUS,
  IDENTITY_STATUS,
  ROLE_ASSIGNMENT_SOURCE_TYPES,
  ROLES,
} from '../constants/enums';

export const actorAuthorityVersionSchema = z.literal('v3.actor-authority.1');
export const actorAuthoritySurfaceStateSchema = z.enum([
  'active',
  'no_identity',
  'no_authority',
  'suspended',
  'archived',
]);
export const actorClassSchema = z.enum(['executable', 'collective', 'external']);
export const authorizationPathTypeSchema = z.enum([
  'direct_role_assignment',
  'membership_role_assignment',
  'delegation_grant',
  'system',
]);

export const actorAuthorityActorSchema = z.object({
  id: z.string().min(1),
  actor_type: z.enum(ACTOR_TYPES),
  actor_class: actorClassSchema,
  display_name: z.string().min(1),
  status: z.enum(ACTOR_STATUS),
  default_user_id: z.string().nullable(),
  created_at: z.string().min(1),
  updated_at: z.string().min(1),
});

export const actorAuthorityIdentitySchema = z.object({
  id: z.string().min(1),
  provider: z.string().min(1),
  provider_subject: z.string().min(1),
  email: z.string().email().nullable(),
  status: z.enum(IDENTITY_STATUS),
  last_seen_at: z.string().nullable(),
  created_at: z.string().min(1),
  active_now: z.boolean(),
});

export const actorAuthorityRoleSchema = z.object({
  id: z.string().min(1),
  name: z.enum(ROLES),
  description: z.string().nullable(),
});

export const actorAuthorityMembershipSchema = z.object({
  id: z.string().min(1),
  member_actor_id: z.string().min(1),
  parent_actor_id: z.string().min(1),
  parent_actor_display_name: z.string().nullable(),
  parent_actor_type: z.enum(ACTOR_TYPES).nullable(),
  membership_type: z.enum(ACTOR_MEMBERSHIP_TYPES),
  context_id: z.string().nullable(),
  starts_at: z.string().nullable(),
  expires_at: z.string().nullable(),
  status: z.enum(AUTHORITY_STATUSES),
  active_now: z.boolean(),
});

export const actorAuthorityRolePathSchema = z.object({
  id: z.string().min(1),
  path_type: z.enum(['direct_role_assignment', 'membership_role_assignment']),
  actor_id: z.string().min(1),
  source_actor_id: z.string().min(1),
  source_actor_display_name: z.string().nullable(),
  role_assignment_id: z.string().min(1),
  membership_id: z.string().nullable(),
  role: actorAuthorityRoleSchema,
  context_id: z.string().nullable(),
  resource_type: z.string().nullable(),
  resource_id: z.string().nullable(),
  source_type: z.enum(ROLE_ASSIGNMENT_SOURCE_TYPES),
  source_id: z.string().nullable(),
  starts_at: z.string().nullable(),
  expires_at: z.string().nullable(),
  status: z.enum(AUTHORITY_STATUSES),
  active_now: z.boolean(),
});

export const actorAuthorityDelegationGrantSchema = z.object({
  id: z.string().min(1),
  delegator_actor_id: z.string().min(1),
  delegator_display_name: z.string().nullable(),
  delegate_actor_id: z.string().min(1),
  context_id: z.string().nullable(),
  capability: z.string().min(1),
  resource_type: z.string().nullable(),
  resource_id: z.string().nullable(),
  ceiling_policy_id: z.string().nullable(),
  starts_at: z.string().nullable(),
  expires_at: z.string().nullable(),
  status: z.enum(AUTHORITY_STATUSES),
  active_now: z.boolean(),
});

export const actorAuthorityPolicySchema = z.object({
  id: z.string().min(1),
  context_id: z.string().nullable(),
  capability: z.string().min(1),
  resource_type: z.string().nullable(),
  risk_level: z.enum(ACTION_POLICY_RISK_LEVELS),
  approval_mode: z.enum(APPROVAL_MODES),
  required_approver_count: z.number().int().min(0),
  requires_distinct_approver: z.number().int().min(0).max(1),
  policy_rank: z.number().int(),
  version: z.number().int().min(1),
  status: z.enum(AUTHORITY_STATUSES),
});

export const actorAuthorityCredentialGrantSchema = z.object({
  id: z.string().min(1),
  credential_id: z.string().min(1),
  connector_id: z.string().nullable(),
  actor_id: z.string().nullable(),
  context_id: z.string().nullable(),
  package_id: z.string().nullable(),
  resource_type: z.string().nullable(),
  resource_id: z.string().nullable(),
  tool_name: z.string().min(1),
  capability: z.string().min(1),
  allowed_scopes: z.array(z.string()),
  scope_hash: z.string().min(1),
  status: z.enum(CREDENTIAL_GRANT_STATUS),
  starts_at: z.string().nullable(),
  expires_at: z.string().nullable(),
  created_by_actor_id: z.string().nullable(),
  active_now: z.boolean(),
});

export const actorAuthorityAuthorizedViaSchema = z.object({
  path_type: authorizationPathTypeSchema,
  id: z.string().min(1),
  membership_id: z.string().nullable(),
  on_behalf_of_actor_id: z.string().nullable(),
  capability: z.string().nullable(),
  context_id: z.string().nullable(),
});

export const actorAuthorityRuntimePrincipalSchema = z.object({
  actor_id: z.string().min(1),
  identity_id: z.string().nullable(),
  requested_context_id: z.string().nullable(),
  requested_capability: z.string().nullable(),
  requested_resource_type: z.string().nullable(),
  requested_risk_level: z.enum(ACTION_POLICY_RISK_LEVELS),
  on_behalf_of_actor_id: z.string().nullable(),
  authorized_via: actorAuthorityAuthorizedViaSchema.nullable(),
  active_role_names: z.array(z.enum(ROLES)),
  active_capabilities: z.array(z.string()),
  approval_mode: z.enum(APPROVAL_MODES),
  required_approver_count: z.number().int().min(0),
  requires_distinct_approver: z.number().int().min(0).max(1),
  credential_grant_ids: z.array(z.string()),
  principal_fingerprint: z.string().min(1),
});

export const actorPassportSummarySchema = z.object({
  identities: z.number().int().min(0),
  active_identities: z.number().int().min(0),
  memberships: z.number().int().min(0),
  active_memberships: z.number().int().min(0),
  role_paths: z.number().int().min(0),
  active_role_paths: z.number().int().min(0),
  delegations: z.number().int().min(0),
  active_delegations: z.number().int().min(0),
  credential_grants: z.number().int().min(0),
  active_credential_grants: z.number().int().min(0),
});

export const actorPassportPacketSchema = z
  .object({
    packet_type: z.literal('actor_passport'),
    version: actorAuthorityVersionSchema,
    generated_at: z.string().min(1),
    surface_state: actorAuthoritySurfaceStateSchema,
    actor: actorAuthorityActorSchema,
    identities: z.array(actorAuthorityIdentitySchema),
    memberships: z.array(actorAuthorityMembershipSchema),
    role_paths: z.array(actorAuthorityRolePathSchema),
    delegation_grants: z.array(actorAuthorityDelegationGrantSchema),
    credential_grants: z.array(actorAuthorityCredentialGrantSchema),
    policy_overrides: z.array(actorAuthorityPolicySchema),
    runtime_principal: actorAuthorityRuntimePrincipalSchema,
    integrity_warnings: z.array(z.string().min(1)),
    summary: actorPassportSummarySchema,
  })
  .superRefine((packet, ctx) => {
    const summary = packet.summary;
    if (
      summary.identities !== packet.identities.length ||
      summary.active_identities !== packet.identities.filter((item) => item.active_now).length ||
      summary.memberships !== packet.memberships.length ||
      summary.active_memberships !== packet.memberships.filter((item) => item.active_now).length ||
      summary.role_paths !== packet.role_paths.length ||
      summary.active_role_paths !== packet.role_paths.filter((item) => item.active_now).length ||
      summary.delegations !== packet.delegation_grants.length ||
      summary.active_delegations !== packet.delegation_grants.filter((item) => item.active_now).length ||
      summary.credential_grants !== packet.credential_grants.length ||
      summary.active_credential_grants !== packet.credential_grants.filter((item) => item.active_now).length
    ) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'summary counts must match packet contents' });
    }
    const actorId = packet.actor.id;
    if (packet.runtime_principal.actor_id !== actorId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'runtime principal must belong to actor' });
    }
    for (const identity of packet.identities) {
      if (packet.runtime_principal.identity_id !== null && packet.runtime_principal.identity_id === identity.id && identity.active_now === false) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'runtime principal identity must be active' });
      }
    }
    for (const path of packet.role_paths) {
      if (path.actor_id !== actorId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'role path actor mismatch' });
      }
      if (path.path_type === 'membership_role_assignment' && !path.membership_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'membership role path requires membership id' });
      }
      if (path.path_type === 'direct_role_assignment' && path.membership_id !== null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'direct role path must not carry membership id' });
      }
    }
    for (const grant of packet.delegation_grants) {
      if (grant.delegate_actor_id !== actorId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'delegation delegate mismatch' });
      }
    }
    for (const grant of packet.credential_grants) {
      if (grant.actor_id !== null && grant.actor_id !== actorId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'credential grant actor mismatch' });
      }
    }
  });

export const actorPrincipalQuerySchema = z.object({
  context_id: z.string().min(1).optional(),
  capability: z.string().min(1).optional(),
  resource_type: z.string().min(1).optional(),
  risk_level: z.enum(ACTION_POLICY_RISK_LEVELS).optional().default('low'),
  on_behalf_of_actor_id: z.string().min(1).optional(),
});

export type ActorAuthorityVersion = z.infer<typeof actorAuthorityVersionSchema>;
export type ActorAuthoritySurfaceState = z.infer<typeof actorAuthoritySurfaceStateSchema>;
export type ActorClass = z.infer<typeof actorClassSchema>;
export type ActorPassportPacket = z.infer<typeof actorPassportPacketSchema>;
export type ActorPrincipalQuery = z.input<typeof actorPrincipalQuerySchema>;
export type ParsedActorPrincipalQuery = z.output<typeof actorPrincipalQuerySchema>;
